import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtUSD, fmtNum, relTime } from "@/lib/format";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ArrowUpRight, ArrowDownRight, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";

export default function ActivityFeed({ limit = 15 }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/feed", { params: { limit } })
      .then(({ data }) => setItems(data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [limit]);

  if (loading) return <p className="text-sm text-gray-500">Loading activity…</p>;
  if (items.length === 0)
    return (
      <p className="text-sm text-gray-500" data-testid="feed-empty">
        Follow other traders on the Leaderboard to see their trades here.
      </p>
    );

  return (
    <div className="divide-y divide-gray-100" data-testid="activity-feed">
      {items.map((it) => (
        <div key={it.id} className="flex items-center gap-3 py-3" data-testid={`feed-item-${it.id}`}>
          <Link to={`/profile/${it.user_id}`}>
            <Avatar className="h-9 w-9 border border-gray-200">
              <AvatarImage src={it.user_avatar} alt={it.user_name} />
              <AvatarFallback className="text-xs">{it.user_name?.slice(0, 2).toUpperCase()}</AvatarFallback>
            </Avatar>
          </Link>
          <div className="flex-1 min-w-0">
            <p className="text-sm truncate">
              <Link to={`/profile/${it.user_id}`} className="font-medium hover:underline">
                {it.user_name}
              </Link>{" "}
              {it.side === "buy" ? (
                <>
                  <ArrowUpRight size={12} className="inline text-gain" />
                  <span className="text-gain font-medium"> bought </span>
                </>
              ) : (
                <>
                  <ArrowDownRight size={12} className="inline text-loss" />
                  <span className="text-loss font-medium"> sold </span>
                </>
              )}
              <span className="font-mono">{fmtNum(it.quantity)}</span>{" "}
              <span className="font-medium">{it.symbol}</span>{" "}
              <span className="text-gray-500">@ {fmtUSD(it.price, { dp: it.price > 10 ? 2 : 4 })}</span>
            </p>
            <p className="text-xs text-gray-500">{relTime(it.created_at)}</p>
          </div>
          {it.grade && (
            <Badge variant="secondary" className="text-[10px] shrink-0">
              <Sparkles size={10} className="mr-1" />
              {it.grade}
            </Badge>
          )}
        </div>
      ))}
    </div>
  );
}
